import { Injectable, Injector, inject } from '@angular/core'
import type { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http'
import { HttpHeaders, HttpResponse } from '@angular/common/http'
import { apiEndpoint, apiEndpointV2, version } from '@awork/environments/environment'
import type { Observable } from 'rxjs'
import { EMPTY, catchError, filter, pipe, map, throwError, takeUntil } from 'rxjs'
import { toObservable } from '@angular/core/rxjs-interop'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { AccountQuery } from '@awork/_shared/state/account.query'
import type { AccountState } from '@awork/_shared/models/account.model'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import { LogService } from '@awork/_shared/services/log-service/log.service'

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private injector = inject(Injector)
  private workspaceQuery = inject(WorkspaceQuery)
  private workspace = this.workspaceQuery.queryCurrentWorkspace()
  private workspaceChanges = toObservable(this.workspace)

  private accountQuery: AccountQuery
  private signalService: SignalService
  private logService: LogService

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.accountQuery = this.injector.get(AccountQuery)
    this.signalService = this.injector.get(SignalService)
    this.logService = this.injector.get(LogService)

    if (req.url.includes(apiEndpoint) || req.url.includes(apiEndpointV2)) {
      // Default request options
      const options = {
        withCredentials: true
      }

      let headers = req.headers ? req.headers : new HttpHeaders()

      // Only account login/refresh endpoints omit bearer authentication and use form data.
      const requestUrl = req.url.split(/[?#]/)[0].replace(/\/$/, '')
      const isAccountAuthentication = [apiEndpoint, apiEndpointV2].some(base =>
        ['token', 'cookie', 'refreshcookie'].some(endpoint => requestUrl === `${base}/accounts/${endpoint}`)
      )

      if (isAccountAuthentication) {
        headers = headers.set('Content-Type', 'application/x-www-form-urlencoded')
      }

      // Required to prevent receiving own websockets
      if (!this.isExcluded(req) && this.signalService.connectionId !== null) {
        headers = headers.set('aw-websocket-connection-id', this.signalService.connectionId)
      }

      // Add required headers
      const account: AccountState = this.accountQuery.getAccount()

      // Native API requests use a bearer token instead of the web login cookie.
      if (account.accessToken !== null && !isAccountAuthentication) {
        const accessToken = account.accessToken

        headers = headers.set('Authorization', `Bearer ${accessToken}`)
      }

      // Set default headers
      headers = headers.set('accept', 'application/json').set('aw-version', version).set('aw-mobile', 'true')

      options['headers'] = headers

      // Clone the request to add the new options
      const newReq = req.clone(options)

      const requestStart = performance.now()

      // Pass on the cloned request instead of the original request.
      const workspaceId = this.workspace()?.id
      const workspaceChanged = this.workspaceChanges.pipe(filter(() => this.workspace()?.id !== workspaceId))

      if (isAccountAuthentication || !workspaceId) {
        return next.handle(newReq).pipe(this.performanceLogger(requestStart, newReq))
      }

      return next.handle(newReq).pipe(
        takeUntil(workspaceChanged),
        // Also guard responses that arrive before the workspace-change effect runs.
        filter(() => this.workspace()?.id === workspaceId),
        catchError(error => {
          if (this.workspace()?.id !== workspaceId) {
            return EMPTY
          }

          return throwError(() => error)
        }),
        this.performanceLogger(requestStart, newReq)
      )
    } else {
      return next.handle(req)
    }
  }

  /**
   * Determines if the call needs to be excluded from sending the websocket connection id.
   * @param {HttpRequest<unknown>} req
   * @returns
   */
  private isExcluded(req: HttpRequest<unknown>) {
    const excludedCalls = [
      {
        url: 'projects', // this generates the task bundles etc. which need to be pushed
        method: 'POST'
      }
    ]
    return excludedCalls.some(excludedCall => {
      return req.url.includes(excludedCall.url) && req.method === excludedCall.method
    })
  }

  /**
   * Custom RxJS pipe to log the performance (duration) of a HTTP GET call
   * @param {number} requestStart
   * @param {HttpRequest<unknown>} request
   * @returns
   */
  private performanceLogger = (requestStart: number, request: HttpRequest<unknown>) =>
    pipe(
      map((response: HttpEvent<unknown>) => {
        if (request.method === 'GET' && response instanceof HttpResponse) {
          const duration = performance.now() - requestStart
          const traceId = response.headers.get('trace-id')
          const url = response.url.split('api/v1/')[1]

          if (duration >= 1000 && traceId) {
            this.logService.logDebug({
              name: 'Long HTTP request:',
              message: `/${url}<br>Duration: ${Math.ceil(duration)}ms<br>traceId: ${traceId}`
            })
          }
        }

        return response
      })
    )
}
